import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FaDownload } from 'react-icons/fa';
import './ExportTable.css';

const ExportTable = ({ data, headers, tableName }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const headerKeyMappings = {
    Certificates: {
      'USER NAME': 'user_name',
      'EMAIL': 'email',
      'COURSE NAME': 'course_name',
      'CERTIFICATE ID': 'certificate_id',
      'ISSUED ON': ['certificate_issued_at', 'issued_at'],
    },
    Course_Assessments: {
      'COURSE ID': 'course_id',
      'COURSE NAME': 'coursename',
      'ASSESSMENT ID 1': 'testid1',
      'ASSESSMENT ID 2': 'testid2',
    },
    Assessment_Request_Notifications: {
      'USER ID': 'user_id',
      'USER EMAIL': 'user_email',
      'COURSE ID': 'course_id',
      'COURSE NAME': 'course_title',
      'ASSESSMENT NUMBER': 'test_number',
      'COMPLETED': 'completed',
    },
    User_Management: {
      'Username': 'username',
      'Email': 'email',
      'Department': 'department_id',
      'Designation': 'designation',
      'Reporting Manager': 'reporting_manager',
      'Region': 'region',
      'Last Accessed(IST)': 'last_login',
      'Admin Action': 'is_admin',
      'Manager Action': 'IsManager',
    },
    Course_Statistics: {
      'COURSE NAME': 'course_name',
      'DEPARTMENT': 'department_name',
      'TOTAL USERS': 'total_users',
      'NO OF VIDEOS': 'video_count',
      'NO OF CERTIFICATES ISSUED': 'certificates_issued',
    },
    Feedback_Overview: {
      'USER ID': 'user_id',
      'USER EMAIL': 'useremail',
      'FEEDBACK TEXT': 'feedback_text',
      'CATEGORY': 'category',
      'SUBMITTED AT': 'submitted_at',
    },
    Departments: {
      'DEPARTMENT NAME': 'department_name',
      'DESCRIPTION': 'description',
      'COURSE COUNT': 'course_count',
      'ENROLLED USERS COUNT': 'enroller_count',
    },
    Dashboard_Users: {
      'Username': 'username',
      'Email': 'email',
      'Designation': 'designation',
      'Department': 'department_name',
      'Reporting Manager': 'reporting_manager',
      'Enrolled Courses': 'courses',
    },
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(tableName, 14, 15);

    const mapping = headerKeyMappings[tableName] || {};

    const tableData = data.map((item) =>
      headers.map((header) => {
        const keys = mapping[header];
        let value = '';

        if (Array.isArray(keys)) {
          for (const key of keys) {
            if (item[key] !== undefined && item[key] !== null) {
              value = item[key];
              break;
            }
          }
        } else {
          value = item[keys] || item[header] || '';
        }

        return String(value || 'N/A');
      })
    );

    autoTable(doc, {
      head: [headers],
      body: tableData,
      startY: 25,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      columnStyles: headers.reduce((acc, _, index) => ({
        ...acc,
        [index]: { cellWidth: 'auto' },
      }), {}),
      didDrawPage: function (data) {
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(10);
        doc.text(
          `Page ${data.pageNumber} of ${pageCount}`,
          doc.internal.pageSize.width / 2,
          doc.internal.pageSize.height - 10,
          { align: 'center' }
        );
      },
    });

    doc.save(`${tableName}_${new Date().toISOString().slice(0, 10)}.pdf`);
    setIsDropdownOpen(false);
  };

  const exportToCSV = () => {
    if (data.length === 0) {
      alert('No data to export');
      return;
    }

    const mapping = headerKeyMappings[tableName] || {};
    const csvHeaders = headers.join(',');

    const csvData = data
      .map((item) =>
        headers
          .map((header) => {
            const keys = mapping[header];
            let value = '';

            if (Array.isArray(keys)) {
              for (const key of keys) {
                if (item[key] !== undefined && item[key] !== null) {
                  value = item[key];
                  break;
                }
              }
            } else {
              value = item[keys] || item[header] || '';
            }

            return String(value || 'N/A');
          })
          .join(',')
      )
      .join('\n');

    const csvContent = `${csvHeaders}\n${csvData}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `${tableName}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove(); // ✅ Sonar fix

    setIsDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="export-table-container" ref={dropdownRef}>
      <button
        className="export-table-download-button"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        aria-label="Export options"
      >
        <FaDownload className="export-table-download-icon" />
      </button>

      {isDropdownOpen && (
        <div className="export-table-dropdown">
          <button className="export-table-dropdown-item" onClick={exportToPDF}>Export to PDF</button>
          <button className="export-table-dropdown-item" onClick={exportToCSV}>Export to CSV</button>
        </div>
      )}
    </div>
  );
};

/* -------- Sonar Clean Prop Validation -------- */

ExportTable.propTypes = {
  tableName: PropTypes.string.isRequired,
  headers: PropTypes.arrayOf(PropTypes.string),
  data: PropTypes.arrayOf(PropTypes.object),
};

ExportTable.defaultProps = {
  headers: [],
  data: [],
};

export default ExportTable;
